import CustomModal from "@components/common/CustomModal";
import CancelDelivery from "@forms/cancelDelivery";
import useDeliveriesCount from "@hooks/useDeliveriesCount";
import { Dialog, DialogContent, DialogContentText } from "@mui/material";
import type { TCancelDelivery } from "@schemas/DeliveryForm/CancelDeliverySchema";
import { DeliveryStatus, DeliveryType } from "@types_def/models/delivery.types";
import type { UserRank } from "@types_def/models/user.type";
import { getAdminChangeDriverDealPath } from "@utils/constants/routes";
import { canClientReactivateCancelledDelivery } from "@utils/delivery/canClientReactivateCancelledDelivery";
import { getDeliveriesCountKeyByStatus } from "@utils/getDeliveriesCountKeyByStatus";
import { enumToStr } from "@utils/strEnum";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCancelDelivery,
  useConfirmDelivery,
  useDeleteDelivery,
  useSendRatingEmail,
  useUpdateDeliveryStatus,
  useUpdateDeliveryType,
} from "@/features/delivery/hooks/use-delivery-actions";

export type ViewDeliveryActionProps = {
  id: number;
  status: DeliveryStatus;
  type: DeliveryType;
  isAdmin?: boolean;
  rank?: UserRank;
  /** Used with {@link canClientReactivateCancelledDelivery} to gate client “reactivate” (see util). */
  cancelledAt?: Date | string | null;
  smsSentAt?: Date | string | null;
};

type DeliveryActionButtonProps = {
  buttonText: string;
  canOpen?: boolean;
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  description?: string;
  disabled?: boolean;
  onConfirm: () => void;
  title: string;
  variant?: "text" | "outlined" | "contained";
};

function useDeliveryActionRoute({ id, isAdmin = false, status, type }: ViewDeliveryActionProps) {
  const navigate = useNavigate();

  return {
    goBack: () => {
      void navigate(-1);
    },
    goToChangeDriver: () => {
      void navigate(getAdminChangeDriverDealPath(enumToStr<DeliveryType>(type), id));
    },
    goToCompletedDetails: () => {
      void navigate(`/admin/pilot/details/${enumToStr<DeliveryType>(type)}/completed/${id}`);
    },
    goToConfirmedDeal: () => {
      void navigate(`/admin/pilot/create-deal/confirmed/${enumToStr<DeliveryType>(type)}/${id}`);
    },
    goToCreateDeal: () => {
      void navigate(
        `/admin/pilot/create-deal/${enumToStr<DeliveryStatus>(status)}/${enumToStr<DeliveryType>(type)}/${id}`
      );
    },
    goToEdit: () => {
      const strType = enumToStr<DeliveryType>(type);
      const strStatus = enumToStr<DeliveryStatus>(status);

      if (isAdmin) {
        void navigate(`/admin/pilot/edit-deal/${strType}/${strStatus}/${id}`);
        return;
      }

      void navigate(`/client/edit-delivery/${strType}/${strStatus}/${id}`);
    },
  };
}

function DeliveryActionButton({
  buttonText,
  canOpen = true,
  color = "primary",
  description = "",
  disabled,
  onConfirm,
  title,
  variant = "contained",
}: DeliveryActionButtonProps) {
  return (
    <CustomModal
      button={buttonText}
      canOpen={canOpen}
      color={color}
      confirmAction={onConfirm}
      confirmText={buttonText}
      disabled={disabled}
      fullWidth={true}
      title={title}
      variant={variant}
    >
      <DialogContentText id="alert-dialog-description">{description}</DialogContentText>
    </CustomModal>
  );
}

export function CancelDeliveryAction(props: ViewDeliveryActionProps) {
  const { id, status } = props;
  const [open, setOpen] = useState(false);
  const { updateDeliveriesCount } = useDeliveriesCount();
  const { mutate: cancelDelivery } = useCancelDelivery();
  const { t } = useTranslation();
  const { goBack } = useDeliveryActionRoute(props);

  const closeDialog = () => {
    setOpen(false);
  };

  const handleCancelDelivery = (data: { id: number; reason: TCancelDelivery }) => {
    const countKey = getDeliveriesCountKeyByStatus(status);

    void cancelDelivery(
      { CancelDeliveryInput: data },
      {
        onSuccess: () => {
          updateDeliveriesCount(countKey, "-");
          updateDeliveriesCount("cancelledCount", "+");
          closeDialog();
          goBack();
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onClose={closeDialog} aria-describedby="alert-dialog-description">
        <DialogContent>
          <CancelDelivery
            handleClose={closeDialog}
            id={id}
            handleCancelDelivery={handleCancelDelivery}
          />
        </DialogContent>
      </Dialog>
      <DeliveryActionButton
        buttonText={t("delivery.action.cancel.text")}
        canOpen={false}
        color="warning"
        description={t("delivery.action.cancel.description")}
        onConfirm={() => {
          setOpen(true);
        }}
        title={`${t("delivery.action.cancel.title")} ${id}`}
      />
    </>
  );
}

export function CancelRestitutionAction(props: ViewDeliveryActionProps) {
  const { id } = props;
  const { mutate: updateDeliveryType } = useUpdateDeliveryType();
  const { t } = useTranslation();
  const { goBack } = useDeliveryActionRoute(props);

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.cancel-restitution.text")}
      color="error"
      description={t("delivery.action.cancel-restitution.description")}
      onConfirm={() => {
        void updateDeliveryType(
          { UpdateDeliveryTypeInput: { id, type: DeliveryType.STANDARD } },
          {
            onSuccess: goBack,
          }
        );
      }}
      title={`${t("delivery.action.cancel-restitution.title")} ${id}`}
    />
  );
}

export function ChangeDriverAction(props: ViewDeliveryActionProps) {
  const { t } = useTranslation();
  const { goToChangeDriver } = useDeliveryActionRoute(props);

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.change-driver.text")}
      color="info"
      description={t("delivery.action.change-driver.description")}
      onConfirm={goToChangeDriver}
      title={t("delivery.action.change-driver.title")}
    />
  );
}
export function SendDeliveryAction(props: ViewDeliveryActionProps) {
  const { t } = useTranslation();
  const { id } = props;

  const { goToCreateDeal } = useDeliveryActionRoute(props);
  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.send-deal.text")}
      canOpen={false}
      color="primary"
      onConfirm={goToCreateDeal}
      title={`${t("delivery.action.send-deal.title")} ${id}`}
    />
  );
}

export function ConfirmDeliveryAction(props: ViewDeliveryActionProps) {
  const { id } = props;
  const { updateDeliveriesCount } = useDeliveriesCount();
  const { mutate: confirmDelivery } = useConfirmDelivery();
  const { t } = useTranslation();
  const { goToConfirmedDeal } = useDeliveryActionRoute(props);

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.confirm.text")}
      color="success"
      description={t("delivery.action.confirm.description")}
      onConfirm={() => {
        void confirmDelivery(
          { ConfirmDeliveryInput: { deliveryId: id } },
          {
            onSuccess: () => {
              updateDeliveriesCount("confirmedCount", "+");
              updateDeliveriesCount("requestedCount", "-");
              goToConfirmedDeal();
            },
          }
        );
      }}
      title={`${t("delivery.action.confirm.title")} ${id}`}
    />
  );
}

export function DeleteDeliveryAction(props: ViewDeliveryActionProps) {
  const { id } = props;
  const { mutate: deleteDelivery } = useDeleteDelivery();
  const { t } = useTranslation();
  const { goBack } = useDeliveryActionRoute(props);

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.delete.text")}
      color="error"
      description={t("delivery.action.delete.description")}
      onConfirm={() => {
        void deleteDelivery(
          { DeleteInput: { id } },
          {
            onSuccess: goBack,
          }
        );
      }}
      title={`${t("delivery.action.delete.title")} ${id}`}
    />
  );
}

export function EditDeliveryAction(props: ViewDeliveryActionProps) {
  const { id } = props;
  const { t } = useTranslation();
  const { goToEdit } = useDeliveryActionRoute(props);

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.edit.text")}
      canOpen={false}
      color="primary"
      onConfirm={goToEdit}
      title={`${t("delivery.action.edit.text")} ${id}`}
    />
  );
}

export function ActivateDeliveryAction(props: ViewDeliveryActionProps) {
  const { id, isAdmin = false, status, cancelledAt, smsSentAt } = props;
  const { updateDeliveriesCount } = useDeliveriesCount();
  const { mutate: updateDeliveryStatus } = useUpdateDeliveryStatus();
  const { t } = useTranslation();
  const { goBack } = useDeliveryActionRoute(props);

  if (
    !isAdmin &&
    !canClientReactivateCancelledDelivery({
      status,
      cancelledAt,
      smsSentAt,
    })
  ) {
    return null;
  }

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.activate.text")}
      color="success"
      description={t("delivery.action.activate.description")}
      onConfirm={() => {
        void updateDeliveryStatus(
          { UpdateDeliveryStatusInput: { id, status: DeliveryStatus.REQUESTED } },
          {
            onSuccess: () => {
              updateDeliveriesCount("cancelledCount", "-");
              updateDeliveriesCount("requestedCount", "+");
              goBack();
            },
          }
        );
      }}
      title={`${t("delivery.action.activate.title")} ${id}`}
    />
  );
}

export function SendRatingEmailAction(props: ViewDeliveryActionProps) {
  const { id } = props;
  const { mutate: sendRatingEmail, isPending: isSendingRatingEmail = false } = useSendRatingEmail();
  const { t } = useTranslation();

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.send-rating-email.text")}
      color="info"
      description={t("delivery.action.send-rating-email.description")}
      disabled={isSendingRatingEmail}
      onConfirm={() => {
        void sendRatingEmail(id);
      }}
      title={`${t("delivery.action.send-rating-email.title")} ${id}`}
    />
  );
}

export function TerminateDeliveryAction(props: ViewDeliveryActionProps) {
  const { id } = props;
  const { updateDeliveriesCount } = useDeliveriesCount();
  const { mutate: updateDeliveryStatus } = useUpdateDeliveryStatus();
  const { t } = useTranslation();
  const { goToCompletedDetails } = useDeliveryActionRoute(props);

  return (
    <DeliveryActionButton
      buttonText={t("delivery.action.terminate.text")}
      color="success"
      description={t("delivery.action.terminate.description")}
      onConfirm={() => {
        void updateDeliveryStatus(
          { UpdateDeliveryStatusInput: { id, status: DeliveryStatus.COMPLETED } },
          {
            onSuccess: () => {
              updateDeliveriesCount("deliveredCount", "+");
              updateDeliveriesCount("onGoingCount", "-");
              goToCompletedDetails();
            },
          }
        );
      }}
      title={`${t("delivery.action.terminate.title")} ${id}`}
    />
  );
}
